import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  Tooltip,
  Typography,
} from "@mui/material";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationSnackbar from "@/components/ui/NotificationSnackbar";
import BlueSwitch from "@/components/ui/BlueSwitch";
import SegmentedPill from "@/components/ui/SegmentedPill";
import VesselAutocomplete from "@/components/ui/VesselAutocomplete";
import {
  CARD_TITLE_SX,
  ROW_LABEL_SX,
  ROW_DESCRIPTION_SX,
  PANEL_CARD_SX,
} from "@/theme/panelStyles";
import logo from "@/assets/images/WetterWeltIcon.png";

const METRIC_ITEMS = [
  {
    key: "Usage",
    label: "Usage",
    singleSelectOnly: true,
    description:
      "Orange one-way, red two-way, blue/green restricted-area borders",
  },
  {
    key: "Navigability",
    label: "Navigability",
    description: "Navigability category: green ECDIS safe → red unsafe",
  },
  {
    key: "airDraft",
    label: "Vertical Clearance",
    description: "Red where the vessel's air draft exceeds the clearance",
  },
  {
    key: "Usability",
    label: "Usability",
    description: "Red = leg not usable for this vessel and voyage",
  },
  {
    key: "ViaAreas",
    label: "Via Areas",
    description: "Red = leg belongs to a currently closed via area",
  },
];

export default function RnwPanelControls({
  analyserStatusLabel,
  applyRnwSafety,
  vesselReady = true,
  vesselOptions = [],
  selectedVessel = null,
  onVesselChange,
  fetchRnwLegs,
  onClearAnalyser,
  hasAnalyserResult = false,
  handleCombinedToggle,
  handleMetricChange,
  hasEnoughPlannerPoints,
  isFetchingAnalyser,
  onSpecificationsClick,
  rnwUiDisabled,
  selectedMetrics,
  setApplyRnwSafety,
  setShowCachedAnalyserToast,
  showCachedAnalyserToast,
  switchCombinedRnw,
  switchRnwAnalyserActive,
  toastPosition,
}) {
  return (
    <>
      <Box sx={{ ...PANEL_CARD_SX, mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Tooltip
              placement="right"
              title={
                <div style={{ display: "flex" }}>
                  <img
                    src={logo}
                    alt="Meteotracker"
                    style={{ height: "40px", marginRight: "8px" }}
                  />
                  <div>
                    Toggles all Route Network (RNW) safety logic: vessel &
                    voyage restrictions, route leg limits and via area closures.
                  </div>
                </div>
              }
            >
              <Typography
                sx={{ ...CARD_TITLE_SX, display: "inline-block", cursor: "help" }}
              >
                RNW Safety
              </Typography>
            </Tooltip>
            <Typography sx={{ ...ROW_DESCRIPTION_SX, mt: 0.15, lineHeight: 1.2 }}>
              {analyserStatusLabel}
            </Typography>
          </Box>

          <SegmentedPill
            options={[
              { value: true, label: "On" },
              { value: false, label: "Off" },
            ]}
            value={applyRnwSafety}
            onChange={setApplyRnwSafety}
          />
        </Box>

        {/* Vessel picker — the analyser needs the vessel's real dimensions.
            Selectable regardless of the RNW Safety toggle. */}
        <Box sx={{ mb: 1 }}>
          <VesselAutocomplete
            options={vesselOptions}
            value={selectedVessel}
            onChange={onVesselChange}
            error={!vesselReady}
            helperText={
              vesselReady
                ? ""
                : selectedVessel
                  ? "Vessel data is incomplete — edit it in Specifications"
                  : "Select a vessel to analyse"
            }
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            variant="contained"
            onClick={fetchRnwLegs}
            disabled={!switchRnwAnalyserActive || isFetchingAnalyser || !vesselReady}
            sx={{
              flex: 1,
              backgroundColor: "#004f7d",
              py: 1.1,
              borderRadius: "12px",
              boxShadow: "0 10px 22px rgba(0, 79, 125, 0.22)",
              "&.Mui-disabled": isFetchingAnalyser
                ? { backgroundColor: "#004f7d", color: "#fff" }
                : {},
            }}
          >
            {isFetchingAnalyser && (
              <CircularProgress size={14} thickness={5} sx={{ color: "#fff", mr: 1 }} />
            )}
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.03em",
              }}
            >
              {isFetchingAnalyser ? "Running..." : "Run analyser"}
            </Typography>
          </Button>
          {hasAnalyserResult && (
            <Tooltip title="Remove the analyser result from the map">
              <Button
                variant="outlined"
                onClick={onClearAnalyser}
                disabled={isFetchingAnalyser}
                sx={{
                  borderRadius: "12px",
                  borderColor: "#004f7d",
                  color: "#004f7d",
                  fontSize: "13px",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                Clear
              </Button>
            </Tooltip>
          )}
        </Box>

        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={onSpecificationsClick}
          disabled={rnwUiDisabled}
          sx={{
            width: "100%",
            borderRadius: "12px",
            borderColor: "rgba(0, 79, 125, 0.22)",
            color: "#004f7d",
            bgcolor: "rgba(255,255,255,0.72)",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
            Specifications
          </Typography>
        </Button>
      </Box>

      <Box sx={PANEL_CARD_SX}>
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={CARD_TITLE_SX}>Display Metrics</Typography>
          <Typography sx={ROW_DESCRIPTION_SX}>
            Choose how the RNW is visualized
          </Typography>
        </Box>

        <List sx={{ ml: 0, mr: 0 }} component="div" disablePadding>
          {METRIC_ITEMS.map((item) => {
            const isDisabled =
              rnwUiDisabled || (item.singleSelectOnly && switchCombinedRnw);
            const isChecked =
              item.singleSelectOnly && switchCombinedRnw
                ? false
                : selectedMetrics[item.key];

            return (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 0.5,
                  py: 0.35,
                  borderRadius: "10px",
                  bgcolor: isChecked ? "rgba(0, 79, 125, 0.08)" : "transparent",
                }}
              >
                <Checkbox
                  size="small"
                  icon={<RadioButtonUncheckedOutlinedIcon />}
                  checkedIcon={<RadioButtonCheckedOutlinedIcon />}
                  checked={isChecked}
                  onChange={handleMetricChange}
                  name={item.key}
                  aria-label={item.label}
                  disabled={isDisabled}
                  sx={{
                    color: isDisabled ? "text.disabled" : "#004f7d",
                    "&.Mui-checked": {
                      color: isDisabled ? "text.disabled" : "#004f7d",
                    },
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      ...ROW_LABEL_SX,
                      color: isDisabled ? "text.disabled" : ROW_LABEL_SX.color,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      ...ROW_DESCRIPTION_SX,
                      color: isDisabled
                        ? "text.disabled"
                        : ROW_DESCRIPTION_SX.color,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          <Divider sx={{ my: 1.1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 0.5,
              py: 0.35,
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...ROW_LABEL_SX,
                  color: rnwUiDisabled ? "text.disabled" : ROW_LABEL_SX.color,
                }}
              >
                Show Combinations
              </Typography>
              <Typography sx={ROW_DESCRIPTION_SX}>
                Combine multiple metrics
              </Typography>
            </Box>
            <BlueSwitch
              sx={{ color: "#004f7d" }}
              checked={switchCombinedRnw}
              disabled={rnwUiDisabled}
              onChange={handleCombinedToggle}
              slotProps={{ input: { "aria-label": "controlled" } }}
            />
          </Box>
        </List>
      </Box>

      <NotificationSnackbar
        open={showCachedAnalyserToast}
        severity="info"
        variant="filled"
        message="Analyser result already up to date"
        onClose={() => setShowCachedAnalyserToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          top: `${toastPosition.top}px !important`,
          right: `${toastPosition.right}px !important`,
        }}
        alertSx={{
          backgroundColor: "rgba(0, 79, 125, 0.56)",
          color: "#fff",
          borderRadius: "18px",
          "& .MuiAlert-icon": { color: "#fff" },
          boxShadow: "0 8px 20px rgba(0, 79, 125, 0.12)",
          backdropFilter: "blur(8px)",
        }}
      />
    </>
  );
}
